import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, message } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout Components
import AppLayout from './components/Layout/AppLayout';
import AuthLayout from './components/Layout/AuthLayout';

// Landing Page
import LandingPage from './pages/Landing/LandingPage';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import VerifyEmail from './pages/Auth/VerifyEmail';

// Client Pages
import Dashboard from './pages/Client/Dashboard';
import VoucherCatalog from './pages/Client/VoucherCatalog';
import VoucherDetails from './pages/Client/VoucherDetails';
import Orders from './pages/Client/Orders';
import OrderDetails from './pages/Client/OrderDetails';
import AccountTopUp from './pages/Client/AccountTopUp';
import AccountRepay from './pages/Client/AccountRepayment';
import AccountTransactions from './pages/Client/AccountTransactions';
import MyAccount from './pages/Client/MyAccount';

// Legal Pages
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import Security from './pages/Legal/Security';
import Support from './pages/Legal/Support';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import ClientManagement from './pages/Admin/ClientManagement';
import VoucherManagement from './pages/Admin/VoucherManagement';
import PricingTierManagement from './pages/Admin/PricingTierManagement';

import OrderManagement from './pages/Admin/OrderManagement';
import SupplierOps from './pages/Admin/SupplierOps';
import SupplyDashboard from './pages/Admin/SupplyDashboard';
import TopUpRequestManagement from './pages/Admin/TopUpRequestManagement';
import RepaymentRequestManagement from './pages/Admin/RepaymentRequestManagement';

// Loading Component
import LoadingSpinner from './components/Common/LoadingSpinner';

// Configure message
message.config({
  top: 64,
  duration: 3,
  maxCount: 3,
});

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    // Redirect to appropriate login page based on current path
    const isAdminPath = window.location.pathname.startsWith('/admin');
    return <Navigate to={isAdminPath ? "/admin/login" : "/login"} replace />;
  }

  if (requiredRole && user.role !== requiredRole && !user.role.startsWith(requiredRole)) {
    // Check if user is admin but route requires admin
    if (requiredRole === 'admin' && (user.isAdmin || user.role === 'super_admin' || user.role === 'admin')) {
      return children; // Allow access for admin users
    }
    
    // Redirect based on user role
    const redirectPath = (user.isAdmin || user.role === 'admin' || user.role === 'super_admin') ? '/admin/dashboard' : '/client/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Root Route Component (only redirects logged-in users)
const RootRoute = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (user) {
    const isAdmin = user.isAdmin || user.role === 'admin' || user.role === 'super_admin';
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/client/dashboard"} replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children, isAdminLogin = false, isLandingPage = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // For Landing Page, show to everyone but redirect authenticated users
  if (isLandingPage && user) {
    // Check if user is admin
    const isAdmin = user.isAdmin || user.role === 'admin' || user.role === 'super_admin';
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/client/dashboard"} replace />;
  }

  // For other public routes (login, register), redirect if authenticated
  if (user && !isLandingPage) {
    // Check if user is admin
    const isAdmin = user.isAdmin || user.role === 'admin' || user.role === 'super_admin';
    
    // Redirect based on user role and login type
    if (isAdminLogin && isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (!isAdminLogin && !isAdmin) {
      return <Navigate to="/client/dashboard" replace />;
    } else if (isAdminLogin && !isAdmin) {
      return <Navigate to="/client/dashboard" replace />;
    } else {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={
        <RootRoute>
          <LandingPage />
        </RootRoute>
      } />

      {/* Client Login Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <AuthLayout>
            <Login />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/register" element={
        <PublicRoute>
          <AuthLayout>
            <Register />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/forgot-password" element={
        <PublicRoute>
          <AuthLayout>
            <ForgotPassword />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/reset-password" element={
        <PublicRoute>
          <AuthLayout>
            <ResetPassword />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/verify-email" element={
        <AuthLayout>
          <VerifyEmail />
        </AuthLayout>
      } />

      {/* Admin Login Route */}
      <Route path="/admin/login" element={
        <PublicRoute isAdminLogin={true}>
          <AuthLayout>
            <Login isAdminLogin={true} />
          </AuthLayout>
        </PublicRoute>
      } />

      {/* Protected Client Routes */}
      <Route path="/client/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/vouchers" element={
        <ProtectedRoute>
          <AppLayout>
            <VoucherCatalog />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/vouchers/:id" element={
        <ProtectedRoute>
          <AppLayout>
            <VoucherDetails />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/cart" element={<Navigate to="/client/vouchers" replace />} />
      
      <Route path="/client/orders" element={
        <ProtectedRoute>
          <AppLayout>
            <Orders />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/orders/:id" element={
        <ProtectedRoute>
          <AppLayout>
            <OrderDetails />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/account/topup" element={
        <ProtectedRoute>
          <AppLayout>
            <AccountTopUp />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/client/account/repayment" element={
        <ProtectedRoute>
          <AppLayout>
            <AccountRepay />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/client/account/repay" element={<Navigate to="/client/account/repayment" replace />} />
      
      <Route path="/client/account/transactions" element={
        <ProtectedRoute>
          <AppLayout>
            <AccountTransactions />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/client/account/profile" element={
        <ProtectedRoute>
          <AppLayout>
            <MyAccount />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Protected Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <AdminDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/clients" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <ClientManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/vouchers" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <VoucherManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/supply-dashboard" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <SupplyDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/pricing-tiers" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <PricingTierManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/orders" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <OrderManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/supplier-ops" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <SupplierOps />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/topup-requests" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <TopUpRequestManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/repayment-requests" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <RepaymentRequestManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/client/dashboard" replace />} />
      <Route path="/vouchers" element={<Navigate to="/client/vouchers" replace />} />
      <Route path="/cart" element={<Navigate to="/client/vouchers" replace />} />
      <Route path="/orders" element={<Navigate to="/client/orders" replace />} />
      <Route path="/profile" element={<Navigate to="/client/account/profile" replace />} />
      
      <Route path="/account/topup" element={<Navigate to="/client/account/topup" replace />} />
      <Route path="/account/repayment" element={<Navigate to="/client/account/repayment" replace />} />
      <Route path="/account/repay" element={<Navigate to="/client/account/repayment" replace />} />
      <Route path="/account/transactions" element={<Navigate to="/client/account/transactions" replace />} />
      
      {/* Default routes */}
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />
      
      {/* Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/security" element={<Security />} />
      <Route path="/support" element={<Support />} />

      {/* 404 */}
      <Route path="*" element={
        <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h1>404 - Page Not Found</h1>
            <p>The page you are looking for doesn't exist.</p>
            <div style={{ marginTop: '20px' }}>
              <a href="/" style={{ color: '#1890ff', textDecoration: 'none' }}>
                ← Back to Home
              </a>
            </div>
          </div>
        </Layout>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
